import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ContactType, FindAllContactsFilters, FindContactsDatabaseResponse } from "../../../../lib/filters/dto";
import { keycloak } from "../../../../lib/keycloak";
import { useFindAllPreferencesQuery, useUpdatePreferencesMutation } from "../../../../lib/preferences/api";
import { peopleColumns, PreferencesSection, toVerifyPeopleColumns } from "../../../../lib/preferences/dto";
import { DatabaseSection, TargetSection } from "../../../../lib/utils";
import { colors } from "../../../../ui/colors";
import { CheckCircleIcon } from "../../../../ui/icons/checkCircle";
import { CircleIcon } from "../../../../ui/icons/circle";
import { EditIcon } from "../../../../ui/icons/edit";
import { LinkedinIcon } from "../../../../ui/icons/linkedin";
import { TrashIcon } from "../../../../ui/icons/trash";
import { UserPlusIcon } from "../../../../ui/icons/userPlus";
import Checkbox from "../../../../ui/molecules/checkbox";
import Pills from "../../../../ui/molecules/pills";
import { Dropdown } from "../../../../ui/organisms/dropdown";
import { CustomTable } from "../../../../ui/organisms/table";
import { DeleteContact } from "../../commons/delete";
import { PersonDTO } from "../dto";
import { PeopleTabValue } from "../editPerson";
import { AssociateCompanyModal } from "../editPerson/associateCompany";

interface Props {
    filters: FindAllContactsFilters
    updateFilters: (value: Partial<FindAllContactsFilters>) => void
    contacts: FindContactsDatabaseResponse<ContactType.PERSON>
    section: DatabaseSection | TargetSection
    selectedIds: string[]
    setSelectedIds: (ids: string[]) => void
}

export function PeopleTable(props: Props) {
    const { filters, updateFilters, contacts, section, selectedIds, setSelectedIds } = props
    const [showDeletePeopleModal, setShowDeletePeopleModal] = useState(false)
    const [showAssociateCompanyModal, setShowAssociateCompanyModal] = useState(false)
    const [personToAssociate, setPersonToAssociate] = useState<PersonDTO | undefined>(undefined)
    const navigate = useNavigate()

    const { data: preferencesResponse } = useFindAllPreferencesQuery(keycloak.subject || "", {
        skip: !keycloak.subject,
    });
    const [updatePreferences] = useUpdatePreferencesMutation();

    const getColumnWidth = (columnName: string, defaultWidth: number = 120, isTargetList = false): number => {
        const preferences = section === "verify-list"
            ? preferencesResponse?.data[0]?.toVerifyBinPersonPreferences
            : preferencesResponse?.data[0]?.personPreferences;
        return preferences?.find(col => col.column === columnName)?.width ?? defaultWidth;
    };

    const navigatePath = new Map<DatabaseSection, string>([
        ['database', "/database/people"],
        ['verify-list', "/verify-bins/people"],
        ['black-list', "/black-list/people"],
    ]);
    return (
        <>
            <CustomTable
                asc={filters.order}
                settedOrder={filters.sort}
                tableHeight={section === "verify-list" ? "calc(100% - 89px)" : "calc(100% - 48px)"}
                prevOrderHeads={[
                    <th className="">
                        <Checkbox
                            shape={"square"}
                            checked={
                                contacts.data.every((person) => selectedIds.includes(person.id))!
                            }
                            onCheck={function (value: boolean): void {
                                if (
                                    contacts.data
                                        .every((person) => selectedIds.includes(person.id))
                                ) {
                                    let newSelected = [...selectedIds];
                                    contacts.data.forEach((person) => {
                                        newSelected = newSelected.filter(
                                            (id) => person.id !== id
                                        );
                                    });
                                    setSelectedIds([...newSelected]);
                                } else {
                                    setSelectedIds([
                                        ...selectedIds,
                                        ...contacts.data
                                            .filter(
                                                (person) =>
                                                    !selectedIds.includes(person.id)
                                            )
                                            .map((person) => person.id)!,
                                    ]);
                                }
                            }}
                        />
                    </th>
                ]}
                postOrderHeads={[<th></th>]}
                preferences={
                    section === "verify-list" ?
                        preferencesResponse?.data[0]?.[PreferencesSection.toVerifyBinPersonPreferences]?.map(pref => ({ ...pref, order: toVerifyPeopleColumns.find(col => col.column === pref.column)?.order })) ?? [] :
                        preferencesResponse?.data[0]?.[PreferencesSection.personPreferences]?.map(pref => ({ ...pref, order: peopleColumns.find(col => col.column === pref.column)?.order })) ?? []
                }
                setPreferences={(items) => {
                    if (preferencesResponse?.data[0]) {
                        updatePreferences({
                            data: {
                                owner: preferencesResponse.data[0].owner!,
                                personPreferences: section === "verify-list" ? preferencesResponse.data[0].personPreferences! : items,
                                companyPreferences: preferencesResponse.data[0].companyPreferences!,
                                targetListPreferences: preferencesResponse.data[0].targetListPreferences!,
                                targetListCompanyPreferences: preferencesResponse.data[0].targetListCompanyPreferences!,
                                targetListPersonPreferences: preferencesResponse.data[0].targetListPersonPreferences!,
                                toVerifyBinPersonPreferences: section === "verify-list" ? items : preferencesResponse.data[0].toVerifyBinPersonPreferences!,
                                toVerifyBinCompanyPreferences: preferencesResponse.data[0].toVerifyBinCompanyPreferences!,
                            },
                            id: preferencesResponse.data[0].id!,
                        });
                    }
                }}
                setOrderFilters={(order) => {
                    updateFilters({ order: !filters.order, sort: order })
                }}
                rows={contacts.data.map(d => ({
                    key: d.id, data: [
                        {
                            item: <td className="text-text-sm font-semibold text-neutral-600">
                                <Checkbox
                                    shape={"square"}
                                    checked={selectedIds.includes(d.id)}
                                    onCheck={function (value: boolean): void {
                                        if (selectedIds.includes(d.id)) {
                                            setSelectedIds([
                                                ...selectedIds.filter((id) => id !== d.id),
                                            ]);
                                        } else {
                                            setSelectedIds([...selectedIds, d.id]);
                                        }
                                    }}
                                />
                            </td>,
                            group: 'preSort',
                            key: 'checkbox'
                        },
                        {
                            item: <td >
                                <div
                                    className="text-text-sm text-red-700 font-semibold crop-text"
                                    style={{ width: getColumnWidth('Esito', 120) }}
                                >
                                    {d.toVerifyOutcomes !== null ? d.toVerifyOutcomes.join(', ') : "-"}
                                </div>
                            </td>,
                            group: 'sort',
                            key: 'Esito'
                        },
                        {
                            item: <td >
                                <div
                                    onClick={() => navigate(`${navigatePath.get(section as DatabaseSection)}/${d.id}/${PeopleTabValue.form}`)}
                                    className="crop-text text-text-sm cursor-pointer font-semibold text-neutral-800 flex flex-row gap-1 items-center"
                                    style={{ width: getColumnWidth('Contatto', 150) }}
                                >
                                    {d.name !== null ? d.name : <Pills size={"xs"} label={"-"} outline={true} emphasis={false} color={"red"} />} {d.surname !== null ? d.surname : <Pills size={"xs"} label={"-"} outline={true} emphasis={false} color={"red"} />}
                                </div>
                            </td>,
                            group: 'sort',
                            key: 'Contatto'
                        },
                        {
                            item: <td >
                                <a
                                    rel="noreferrer"
                                    target="_blank"
                                    href={"tel:" + d.phone}
                                >
                                    <div className="crop-text text-text-sm text-neutral-600" style={{ width: getColumnWidth('Smartphone', 150) }}>
                                        {d.phone ?? <Pills size={"xs"} label={"-"} outline={true} emphasis={false} color={"red"} />}
                                    </div>
                                </a>
                            </td>,
                            group: 'sort',
                            key: 'Smartphone'
                        },
                        {
                            item: <td >
                                <a
                                    rel="noreferrer"
                                    target="_blank"
                                    href={"mailto:" + d.emails}
                                    className="w-min"
                                >
                                    <div className="crop-text text-text-sm text-neutral-600" style={{ width: getColumnWidth('Email', 200) }}>
                                        {d.emails ?? <Pills size={"xs"} label={"-"} outline={true} emphasis={false} color={"red"} />}
                                    </div>
                                </a>
                            </td>,
                            group: 'sort',
                            key: 'Email'
                        },
                        {
                            item: <td >
                                <div
                                    onClick={() => navigate(`${navigatePath.get(section as DatabaseSection)}/${d.id}/${PeopleTabValue.company}`)}
                                    //TODO RESET EDIT COMPANY REQUEST
                                    //TODO CHECK IF RESET ERROR IN FORMS WITH ZOD
                                    className="block cursor-pointer crop-text text-text-sm font-semibold text-brandPrimary-500"
                                    style={{ width: getColumnWidth('Azienda', 150) }}
                                >
                                    {d.companyName ?? <Pills size={"xs"} label={"-"} outline={true} emphasis={false} color={"red"} />}
                                </div>
                            </td>,
                            group: 'sort',
                            key: 'Azienda'
                        },
                        {
                            item: <td className=" relative group">
                                {d.particularities && d.particularities.length > 0 ? (
                                    <div className="text-text-sm font-semibold text-neutral-600 crop-text" style={{ width: getColumnWidth('Particolarità', 100, true) }}>
                                        {(d.particularities[0] + (d.particularities.length > 1 ? (', ..., + ' + (d.particularities.length - 1)) : ''))}
                                    </div>
                                ) : (
                                    "-"
                                )}
                                {d.particularities && d.particularities.length > 0 && <div className="absolute left-0 bottom-full z-50 hidden group-hover:block text-neutral-700 bg-neutral-200 rounded-md p-1 text-text-xs">
                                    {d.particularities.map((item, index) => (
                                        <span key={index}>
                                            {item}
                                            <br />
                                        </span>
                                    ))}
                                </div>}
                            </td>,
                            group: 'sort',
                            key: 'Particolarità'
                        },
                        {
                            item: <td >
                                <div>
                                    <Pills
                                        size={"xs"}
                                        label={d.active ? "Attivo" : "Inattivo"}
                                        outline={false}
                                        emphasis={false}
                                        color={d.active ? "green" : "blue"}
                                    />
                                </div>
                            </td>,
                            group: 'sort',
                            key: 'Stato'
                        },
                        {
                            item: <td >
                                {
                                    d.linkedinProfile ?
                                        <a
                                            rel="noreferrer"
                                            target="_blank"
                                            href={d.linkedinProfile}
                                            className="w-full flex"
                                        >
                                            <LinkedinIcon color={colors.neutral[400]} size={20} />
                                        </a> : <span className="text-text-sm text-neutral-600" style={{ width: getColumnWidth('Linkedin', 150) }}>{<Pills size={"xs"} label={"-"} outline={true} emphasis={false} color={"red"} />}</span>
                                }
                            </td>,
                            group: 'sort',
                            key: 'Linkedin'
                        },
                        {
                            item: <td >
                                <div className="crop-text text-text-sm text-neutral-600" style={{ width: getColumnWidth('Ruolo', 150) }}>{d.role ?? <Pills size={"xs"} label={"-"} outline={false} emphasis={false} color={"red"} />}</div>
                            </td>,
                            group: 'sort',
                            key: 'Ruolo'
                        },
                        {
                            item: <td >
                                <div className="crop-text max-w-[150px] text-text-sm text-neutral-600" style={{ width: getColumnWidth('Reparto', 150) }}>
                                    {d.department ?? <Pills size={"xs"} label={"-"} outline={true} emphasis={false} color={"red"} />}
                                </div>
                            </td>,
                            group: 'sort',
                            key: 'Reparto'
                        },
                        {
                            item: <td >
                                <div className="w-full flex justify-center">
                                    {d.privacy ? (
                                        <CheckCircleIcon color={colors.neutral[400]} size={20} />
                                    ) : (
                                        <CircleIcon color={colors.neutral[400]} size={20} />
                                    )}
                                </div>
                            </td>,
                            group: 'sort',
                            key: 'Privacy'
                        },
                        {
                            item: <td className="w-[20px] cursor-pointer z-20">
                                <Dropdown
                                    items={[
                                        {
                                            label: "Modifica",
                                            icon: <EditIcon color={""} size={0} />,
                                            onClick: () => navigate(`${navigatePath.get(section as DatabaseSection)}/${d.id}/${PeopleTabValue.form}`)
                                        },
                                        d.companyId === null
                                            ? {
                                                label: "Associa azienda",
                                                icon: <UserPlusIcon color={""} size={0} />,
                                                onClick: () => {
                                                    setShowAssociateCompanyModal(true)
                                                    setPersonToAssociate(d)
                                                },
                                            }
                                            : null,
                                        window.location.pathname.includes('black-list') || keycloak.hasRealmRole('commercial') ?
                                            null :
                                            {
                                                label: "Cancella",
                                                icon: <TrashIcon color={""} size={0} />,
                                                onClick: () => {
                                                    setSelectedIds([d.id])
                                                    setShowDeletePeopleModal(true)
                                                },
                                            },
                                    ]}
                                />
                            </td>,
                            group: 'postSort',
                            key: 'menu'
                        }
                    ]
                })) ?? []}
            />
            <AssociateCompanyModal
                visible={showAssociateCompanyModal}
                onClose={() => setShowAssociateCompanyModal(false)}
                operation="associate"
                person={personToAssociate}
            />
            <DeleteContact
                contactType={ContactType.PERSON}
                showModal={showDeletePeopleModal}
                setShowModal={setShowDeletePeopleModal}
                selectedIds={selectedIds}
                setSelectedIds={setSelectedIds}
            />
        </>
    )
}